<template>
  <SearchableSelectComponent
    :options="options"
    :modelValue="model"
    placeholder="Search accounts…"
    :is-loading="loading"
    :is-error="failed"
    @update:model-value="select"
    @query-updated="fetchAccounts"
  />
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
interface AdminAccount {
  id: number;
  email: string;
  name?: string | null;
  internal_role?: string;
}
const emit = defineEmits<{
  select: [account: AdminAccount];
}>();
const model = defineModel<number | null>();
const accounts = ref<AdminAccount[]>([]);
const loading = ref(false);
const failed = ref(false);
let requestId = 0;
const { globalErrorHandler } = useGlobalHandlers();
const options = computed(() =>
  accounts.value
    .filter(
      (account) =>
        (!loading.value && !failed.value) || account.id === model.value
    )
    .map((account) => ({
      label: account.email,
      value: account.id,
      description: `${account.name || "No name"} · ID: ${account.id}`,
    }))
);
async function fetchAccounts(search = "") {
  const currentRequestId = ++requestId;
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await goSolidApi.get("/api/admin/accounts", {
      params: { search },
    });
    if (currentRequestId !== requestId) return;
    accounts.value = Array.isArray(data) ? data : Object.values(data);
  } catch (error) {
    if (currentRequestId !== requestId) return;
    failed.value = true;
    globalErrorHandler(error);
  } finally {
    if (currentRequestId === requestId) loading.value = false;
  }
}
function select(value: number | null) {
  const account = accounts.value.find((item) => item.id === value);
  if (account) emit("select", account);
  model.value = value;
}
onMounted(() => fetchAccounts());
</script>
